import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { HdBodygraphCenter, HdBodygraphChartPreset } from '../hd-bodygraph.models';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import {
  HdChannelListItemComponent,
  HdChannelListChannelInfoRequest,
  HdChannelListGateInfoRequest,
} from '../channel-list/hd-channel-list-item.component';
import { resolveHdCycleOverlaySource } from '../hd-cycle-overlay.utils';
import {
  HdCycleCardSlot,
  buildHdCycleChannelCardSources,
  buildHdCycleConditionedCenters,
  buildHdCycleHangingGateCardSources,
  sortByHdCyclePriority,
  toHdCycleCardSlots,
} from '../hd-cycle-cards.builder';
import {
  HdCycleChannelVm,
  HdCycleHangingGateVm,
  buildHdCycleKeynotes,
} from '../hd-cycle-keynotes.builder';
import {
  HD_CYCLE_ENCOUNTER_KIND_FA_ICONS,
  HD_CYCLE_MAIN_KEYNOTES,
  HdCycleMainKeynoteKey,
} from '../hd-cycles-cheat-sheet-metadata';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { HdCollapsibleItems } from './hd-collapsible-items';
import { HdCycleCardsComponent } from './hd-cycle-cards.component';
import {
  HdCycleCenterBlockCause,
  HdCycleCenterBlockInfoRequest,
  HdCycleConditionedCenterBlockComponent,
} from './hd-cycle-conditioned-center-block.component';
import { HdCycleHangingGateBlockComponent } from './hd-cycle-hanging-gate-block.component';
import { HdCyclePlanetGridComponent } from './hd-cycle-planet-grid.component';
import { HdCycleSectionHeaderComponent } from './hd-cycle-section-header.component';

/** Ongedefinieerd natal-center dat door de cycle definitie krijgt. */
interface ConditionedCenterVm {
  readonly center: HdBodygraphCenter;
  readonly label: string;
  /** HTML met `<b>`-accenten (cyclus-specifiek niet-zelf thema uit de cycles-scope). */
  readonly text: string;
  /** Cycle-activaties die het center definiëren (poort-pillen). */
  readonly causes: readonly HdCycleCenterBlockCause[];
}

/** De drie cheat-sheet-keynotes + de gedempte vernieuwings-sectie. */
type KeynoteSectionKey = HdCycleMainKeynoteKey | 'renewal';

/** Header-kleur van de Vernieuwing-sectie: bewust gedempt grijs (geen cyclusthema). */
const RENEWAL_SECTION_COLOR = '#8A8F98';

interface ChannelItemVm {
  readonly key: string;
  readonly channel: HdCycleChannelVm;
  readonly cards: readonly HdCycleCardSlot[];
}

interface HangingItemVm {
  readonly key: string;
  readonly hanging: HdCycleHangingGateVm;
  readonly cards: readonly HdCycleCardSlot[];
}

interface KeynoteSectionVm {
  readonly key: KeynoteSectionKey;
  /** Cheat-sheet-kaartkleur van de keynote (header-balk). */
  readonly color: string;
  /** Cheat-sheet-FA-icoon van de keynote. */
  readonly faIcon: string;
  readonly title: string;
  readonly bringsLabel: string;
  /** HTML met `<b>`-accenten (uit de cycles-scope, via `[innerHTML]`). */
  readonly brings: string;
  readonly bullets: readonly string[];
  readonly channels: readonly ChannelItemVm[];
  readonly hangingGates: readonly HangingItemVm[];
  /** Keynote-accent op de kanaal-items; null voor de vernieuwings-sectie. */
  readonly channelKeynoteKey: HdCycleMainKeynoteKey | null;
  /** Direct accent op de kanaal-items (vernieuwings-sectie); wint van keynoteKey. */
  readonly channelAccent: { color: string; faIcon: string } | null;
  /** Gedempte weergave: vernieuwing is geen cyclusthema, enkel context. */
  readonly muted: boolean;
}

/**
 * De cyclus-keynote-weergaves in het Geavanceerd-paneel van een cycle chart
 * (onder de Cycles-sectie). Bovenaan de "Niet zelf center thema's"-subsectie:
 * elk ongedefinieerd natal-center dat door de cycle definitie krijgt, als
 * klikbaar cheat-sheet-blokje (center-shape + cyclus-specifiek thema + de
 * poort-pillen van de cycle-activaties die het definiëren). Daaronder een
 * toggle tussen twee views op dezelfde keynotes:
 * - **secties**: Persoonlijk (electromagnetic/compromis-kanalen met de natal
 *   chart), Educatief (kanalen die de cyclus op zichzelf brengt) en Omgevend
 *   (hangende cycle-poorten) — uitklapbare cheat-sheet-koppen, kanaal-items
 *   met klikbare poorten, lege secties verdwijnen; achteraan gedempt
 *   **Vernieuwing**: kanalen die de natal chart al volledig heeft en die de
 *   cyclus enkel hernieuwt (geen cyclusthema, weegt niet door). Elk item
 *   heeft rechts een uitklapknop voor de I Tjing-lijnkaarten van de
 *   cycle-poorten (`hd-cycle-cards`, **default dicht en lui gebouwd**);
 * - **grid**: de D/P-planetengrid van het cycle-moment met de drie keynotes
 *   als flanken per activatie ({@link HdCyclePlanetGridComponent}).
 *
 * Een **momentchart** wordt als cyclus behandeld (spec transits 2026-09-26
 * §2.8): dezelfde duiding van wat het moment met de chart doet, met enkel de
 * personality-kant van het moment. Een periode (range-moment zonder vast
 * tijdstip) draagt geen activaties en toont een korte infozin.
 */
@Component({
  selector: 'hd-cycle-keynote-sections',
  templateUrl: './hd-cycle-keynote-sections.component.html',
  styleUrl: './hd-cycle-keynote-sections.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    HdChannelListItemComponent,
    HdCyclePlanetGridComponent,
    HdCycleSectionHeaderComponent,
    HdCycleConditionedCenterBlockComponent,
    HdCycleHangingGateBlockComponent,
    HdCycleCardsComponent,
  ],
})
export class HdCycleKeynoteSectionsComponent {
  /** Preset van de actieve tab; alleen een cycle-preset rendert iets. */
  readonly preset = input<HdBodygraphChartPreset | null>(null);

  readonly gateInfoRequested = output<HdChannelListGateInfoRequest>();
  readonly channelInfoRequested = output<HdChannelListChannelInfoRequest>();
  /** Klik op een "Niet zelf center thema"-blokje → center-info (zoals een klik op het center in de chart). */
  readonly centerInfoRequested = output<HdCycleCenterBlockInfoRequest>();

  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);

  /** Actieve weergave: de keynote-secties of de planetengrid met flanken. */
  protected readonly viewMode = signal<'sections' | 'grid'>('sections');

  /** Uitgeklapte sectie-bullets (sectie-key ⇔ open). */
  private readonly expandedSections = signal<ReadonlySet<KeynoteSectionKey>>(new Set());

  /** Uitklapstaat van de I Tjing-kaarten per item: default dicht (performance van het tabblad). */
  protected readonly items = new HdCollapsibleItems(false);

  constructor() {
    // Beide content-talen eager laden zodat de NL-fallback voor nog niet
    // vertaalde EN-velden meteen correcte tekst oplevert.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /** Cyclusmoment (P+D) of momentchart (enkel P); `null` voor andere charts. */
  private readonly overlay = computed(() => resolveHdCycleOverlaySource(this.preset()));

  /** Periode zonder vast tijdstip: geen activaties, dus geen duiding. */
  protected readonly isRangeMoment = computed(() => {
    const overlay = this.overlay();
    return overlay?.kind === 'moment' && overlay.activations.length === 0;
  });

  protected readonly breakdown = computed(() => {
    const preset = this.preset();
    const overlay = this.overlay();
    if (!preset || !overlay || this.isRangeMoment()) {
      return null;
    }
    return buildHdCycleKeynotes(preset.planetaryActivations ?? [], overlay.activations);
  });

  /** Titel van de "Niet zelf center thema's"-subsectie (cheat-sheet-key; momentvariant op een momentchart). */
  protected readonly centersTitle = computed(() =>
    this.cyclesText(
      this.overlay()?.kind === 'moment' ? 'centerConditioning.momentTitle' : 'centerConditioning.title',
      this.lang(),
    ),
  );

  /** Titel boven de wisselbare keynote-weergaves ("Cyclus details" / "Moment details"). */
  protected readonly detailsTitle = computed(() =>
    this.cyclesText(this.overlay()?.kind === 'moment' ? 'ui.momentDetailsTitle' : 'ui.cycleDetailsTitle', this.lang()),
  );

  protected readonly rangeInfo = computed(() => this.cyclesText('ui.momentRangeInfo', this.lang()));

  /**
   * Ongedefinieerde natal-centers die door de cycle-activaties definitie
   * krijgen, in cheat-sheet-volgorde, met het cyclus-specifieke niet-zelf
   * thema als blokje (center-shape in chartkleur + label + tekst) en de
   * poort-pillen van de cycle-activaties die het center definiëren.
   */
  protected readonly conditionedCenters = computed<readonly ConditionedCenterVm[]>(() => {
    const preset = this.preset();
    const overlay = this.overlay();
    if (!preset || !overlay || overlay.activations.length === 0) {
      return [];
    }
    const lang = this.lang();
    return buildHdCycleConditionedCenters(preset.configuration, overlay.activations, this.renderStateFactory)
      .map(item => ({
        center: item.center,
        label: this.cyclesText(`centerConditioning.centers.${item.center}.label`, lang),
        text: this.cyclesText(`centerConditioning.centers.${item.center}.text`, lang),
        causes: item.causes.map(cause => ({
          glyph: cause.glyph,
          gate: cause.gate,
          line: cause.line,
          title: `${this.planetInfo.planetInfoIn(cause.planetKey, lang)?.name ?? cause.planetKey} ${cause.gate}.${cause.line}`,
        })),
      }));
  });

  protected readonly viewToggleTooltip = computed(() => {
    const key = this.viewMode() === 'sections' ? 'ui.showGridView' : 'ui.showSectionView';
    return this.cyclesText(key, this.lang());
  });

  protected toggleViewMode(): void {
    this.viewMode.update(mode => (mode === 'sections' ? 'grid' : 'sections'));
  }

  /** Alleen de niet-lege secties, in cheat-sheet-volgorde. */
  protected readonly sections = computed<readonly KeynoteSectionVm[]>(() => {
    const breakdown = this.breakdown();
    if (!breakdown) {
      return [];
    }
    const lang = this.lang();
    const bringsLabel = this.cyclesText('bringsLabel', lang);
    const section = (
      key: KeynoteSectionKey,
      channels: readonly HdCycleChannelVm[],
      hangingGates: readonly HdCycleHangingGateVm[],
    ): KeynoteSectionVm => {
      const muted = key === 'renewal';
      const meta = muted
        ? { color: RENEWAL_SECTION_COLOR, faIcon: HD_CYCLE_ENCOUNTER_KIND_FA_ICONS.renewal }
        : HD_CYCLE_MAIN_KEYNOTES.find(candidate => candidate.key === key);
      const color = meta?.color ?? '#3D414E';
      const faIcon = meta?.faIcon ?? '';
      return {
        key,
        color,
        faIcon,
        title: this.cyclesText(`mainKeynotes.${key}.title`, lang),
        bringsLabel,
        brings: this.cyclesText(`mainKeynotes.${key}.brings`, lang),
        bullets: this.cyclesList(`mainKeynotes.${key}.bullets`, lang),
        // Op planeetprioriteit (zon eerst), zoals het Just Now-paneel: een
        // kanaal lift mee op zijn hoogste cycle-planeet (Steven 2026-10-14).
        channels: sortByHdCyclePriority(
          channels.map(channel => ({
            key: `${key}:${channel.channelKey}`,
            channel,
            sources: buildHdCycleChannelCardSources(channel),
          })),
          item => item.sources,
        ).map(({ key: itemKey, channel, sources }) => ({ key: itemKey, channel, cards: toHdCycleCardSlots(sources) })),
        hangingGates: sortByHdCyclePriority(
          hangingGates.map(hanging => ({
            key: `${key}:${hanging.gate}`,
            hanging,
            sources: buildHdCycleHangingGateCardSources(hanging),
          })),
          item => item.sources,
        ).map(({ key: itemKey, hanging, sources }) => ({ key: itemKey, hanging, cards: toHdCycleCardSlots(sources) })),
        channelKeynoteKey: muted ? null : (key as HdCycleMainKeynoteKey),
        channelAccent: muted ? { color, faIcon } : null,
        muted,
      };
    };
    return [
      section('personal', breakdown.personal, []),
      section('educational', breakdown.educational, []),
      section('environmental', [], breakdown.environmental),
      // Gedempt achteraan: kanalen die je al volledig hebt — geen cyclusthema,
      // enkel vernieuwing; zo lijkt zo'n cycle-activatie niet zoek.
      section('renewal', breakdown.renewals, []),
    ].filter(vm => vm.channels.length > 0 || vm.hangingGates.length > 0);
  });

  /** Zichtbaar op elke cycle chart — ook wanneer alle keynote-secties leeg zijn (grid + centers blijven relevant). */
  protected readonly visible = computed(() => this.breakdown() != null);

  protected isExpanded(key: KeynoteSectionKey): boolean {
    return this.expandedSections().has(key);
  }

  protected toggleSection(key: KeynoteSectionKey): void {
    this.expandedSections.update(expanded => {
      const next = new Set(expanded);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  /** Tekst uit de cycles-scope met NL-fallback voor (nog) lege EN-velden. */
  private cyclesText(key: string, lang: string): string {
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }

  private cyclesList(key: string, lang: string): readonly string[] {
    const value = this.gateInfo.cyclesCheatSheetListIn(key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetListIn(key, 'nl');
  }
}
